/**
 * Mail 配信先（mail_contacts）の hooks。
 * 仕様書: docs/01_spec/spec_mail_system.md B-10
 */
import type { ContactPromotionLevel, MailContact } from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit as limitFn,
  orderBy,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import {
  clearCollectionCache,
  fetchCollectionWithCache,
  removeItemFromCollectionCache,
} from '@/lib/collectionCache';
import { db, functions } from '@/lib/firebase';
import { safeDeleteDoc, safeUpdateDoc } from '@/lib/safeWrite';

export interface MailContactRecord extends MailContact {
  id: string;
}

interface UseMailContactsOptions {
  tag?: string;
  limit?: number;
}

/**
 * @param queryOptions.enabled false のとき読み込まない (2026-10-16)。外部SA は全件 read が
 *   rules で拒否されるため、外部SA でも開く画面はここで止める。queryKey には含めない。
 */
export function useMailContacts(
  options?: UseMailContactsOptions,
  queryOptions?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['mail_contacts', options ?? null],
    enabled: queryOptions?.enabled ?? true,
    // 差分同期キャッシュにより自身でフレッシュさを保つので、staleTime を長めにして
    // 不要な React Query 再実行を抑制 (キャッシュ経由なら追加 read 0、ない場合は meta 1 read)
    staleTime: 5 * 60_000, // 5 分
    queryFn: async (): Promise<MailContactRecord[]> => {
      // tag フィルタや小さい limit が指定された場合は従来の直接クエリ
      if (options?.tag || (options?.limit && options.limit < 500)) {
        const conditions = options?.tag ? [where('tags', 'array-contains', options.tag)] : [];
        const snap = await getDocs(
          query(
            collection(db, 'mail_contacts'),
            ...conditions,
            orderBy('updatedAt', 'desc'),
            limitFn(options?.limit ?? 500),
          ),
        );
        return snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as MailContact),
        }));
      }

      // 全件取得相当 → 差分同期キャッシュ経由 (v3.3.x)
      const res = await fetchCollectionWithCache<MailContactRecord>(
        db,
        'mail_contacts',
        (id, data) => ({ id, ...(data as MailContact) }),
      );
      // limit 指定がある場合はクライアント側でスライス
      if (options?.limit) {
        return res.items.slice(0, options.limit);
      }
      return res.items;
    },
  });
}

/** 強制的にキャッシュを破棄して次回 fetch で全件再取得させる */
export function invalidateMailContactsCache(): void {
  clearCollectionCache('mail_contacts');
}

/**
 * v3.2.23+: SA (外部営業) 自身の担当顧客のみ取得 (`ownerSaUid===saUid`)。
 */
export function useSaContacts(saUid: string | null | undefined) {
  return useQuery({
    queryKey: ['mail_contacts', 'sa_owned', saUid],
    enabled: !!saUid,
    queryFn: async (): Promise<MailContactRecord[]> => {
      if (!saUid) return [];
      const snap = await getDocs(
        query(
          collection(db, 'mail_contacts'),
          where('ownerSaUid', '==', saUid),
          orderBy('updatedAt', 'desc'),
        ),
      );
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as MailContact) }));
    },
  });
}

/**
 * 特定グループに所属する配信先のみ取得。
 * mail_contacts.groupIds[] を array-contains で問い合わせるため、
 * 全件ロードせず正確に取得できる。
 */
export function useContactsInGroup(groupId: string | null | undefined) {
  return useQuery({
    queryKey: ['mail_contacts', 'in_group', groupId],
    enabled: !!groupId,
    queryFn: async (): Promise<MailContactRecord[]> => {
      if (!groupId) return [];
      const snap = await getDocs(
        query(
          collection(db, 'mail_contacts'),
          where('groupIds', 'array-contains', groupId),
          orderBy('updatedAt', 'desc'),
        ),
      );
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as MailContact) }));
    },
  });
}

/**
 * 1 件の mail_contact をドキュメント ID で取得 (顧客詳細ページ用)。
 */
export function useMailContact(id: string | null | undefined) {
  return useQuery({
    queryKey: ['mail_contacts', 'detail', id],
    enabled: !!id,
    queryFn: async (): Promise<MailContactRecord | null> => {
      if (!id) return null;
      const snap = await getDoc(doc(db, 'mail_contacts', id));
      if (!snap.exists()) return null;
      return { id: snap.id, ...(snap.data() as MailContact) };
    },
  });
}

export function useDeleteMailContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await safeDeleteDoc(doc(db, 'mail_contacts', id));
      return id;
    },
    onSuccess: (id) => {
      // サーバー側 meta Trigger は非同期 (数秒遅延) のため、削除直後の再取得では
      // 差分同期キャッシュが「変化なし」と判定し削除行が残ってしまう。
      // 自身が起こした削除はローカルキャッシュから直接取り除いて即時反映する。
      removeItemFromCollectionCache('mail_contacts', id);
      qc.invalidateQueries({ queryKey: ['mail_contacts'] });
    },
  });
}

interface UpdateContactArgs {
  id: string;
  data: Partial<
    Pick<
      MailContact,
      | 'name'
      | 'tags'
      | 'defaultEmail'
      | 'emails'
      | 'customFields'
      | 'kind'
      | 'companyName'
      | 'department'
      | 'jobTitle'
      | 'postalCode'
      | 'prefecture'
      | 'address'
      | 'buildingName'
      | 'birthdate'
      | 'salesMemo'
      | 'isTestContact'
      | 'billingAddresses'
      | 'deliveryAddresses'
      | 'firstName'
      | 'lastName'
      | 'firstNameKana'
      | 'lastNameKana'
      | 'phone'
      | 'occupation'
      | 'gender'
      | 'personalAddress'
      | 'nameEn'
      | 'phoneIntl'
      | 'englishAddress'
      | 'ownerSaUid'
      | 'ownerSaName'
      | 'ownerSaAssignedVia'
      | 'ownerSaAssignedAt'
      | 'saRetentionUntil'
      | 'saRetentionMonths'
      | 'saRetentionStartedAt'
      | 'saRetentionReason'
      | 'saRetentionReminderSentFor'
    >
  >;
}

export function useUpdateMailContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: UpdateContactArgs) => {
      await safeUpdateDoc(doc(db, 'mail_contacts', id), {
        ...data,
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail_contacts'] }),
  });
}

interface ExtendSaRetentionArgs {
  contactId: string;
  /** 延長するヶ月数。未指定なら settings/sales の保有期間。 */
  months?: number;
  /** 起算点。'from_now' = 今から / 'from_until' = 現在の期限から上乗せ。既定 'from_now'。 */
  mode?: 'from_now' | 'from_until';
}

const extendSaRetentionFn = httpsCallable<
  ExtendSaRetentionArgs,
  { ok: true; retentionUntil: string; months: number }
>(functions, 'extendSaRetention');

/**
 * SA 顧客保有期限を手動で延長する (owner 限定 Callable)。
 * 顧客詳細 > 基本情報 > 担当SA から実行する。
 */
export function useExtendSaRetention() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: ExtendSaRetentionArgs) => (await extendSaRetentionFn(args)).data,
    onSuccess: () => {
      invalidateMailContactsCache();
      void qc.invalidateQueries({ queryKey: ['mail_contacts'] });
    },
  });
}

/**
 * 配信先を 1 件手動追加（addMailContactManual Callable 経由）。
 * 重複メアドはサーバ側でエラーになる。
 */
interface CreateContactArgs {
  name?: string;
  email: string;
  tags?: string[];
  customFields?: Record<string, string>;
  /** true の場合、追加と同時に promotionLevel='customer' で登録 */
  promoteToCustomer?: boolean;
}

const addMailContactManualFn = httpsCallable<CreateContactArgs, { ok: boolean; contactId: string }>(
  functions,
  'addMailContactManual',
);

export function useCreateMailContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: CreateContactArgs) => {
      const res = await addMailContactManualFn(args);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail_contacts'] }),
  });
}

/**
 * promotionLevel を手動で変更（lead↔customer↔inactive）。
 * サーバ側 manuallyChangePromotionLevel Callable がフィールド整合
 * （promotedToCustomerAt / inactivatedAt の付替）まで担うのでクライアントは呼ぶだけで良い。
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §3.2, §4.4
 */
interface PromoteContactArgs {
  contactId: string;
  to: ContactPromotionLevel;
  reason?: string;
}

const manuallyChangePromotionLevelFn = httpsCallable<
  PromoteContactArgs,
  { id: string; to: ContactPromotionLevel }
>(functions, 'manuallyChangePromotionLevel');

export function usePromoteMailContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: PromoteContactArgs) => {
      const res = await manuallyChangePromotionLevelFn(args);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail_contacts'] }),
  });
}

// ─────────────────────────────────────────────
// 無効アドレスの特定・クレンジング（B-20）
// ─────────────────────────────────────────────
export interface InvalidContactRow {
  email: string;
  reason: string;
  contactId: string | null;
  contactName: string | null;
  otherEmailCount: number;
}

const findInvalidEmailContactsFn = httpsCallable<
  Record<string, never>,
  { ok: boolean; total: number; matchedContacts?: number; rows: InvalidContactRow[] }
>(functions, 'findInvalidEmailContacts');

/** mail_send_logs から無効アドレスを抽出し、該当 contact をレポート（削除しない）。 */
export function useFindInvalidEmailContacts() {
  return useMutation({
    mutationFn: async () => (await findInvalidEmailContactsFn({})).data,
  });
}

const cleanupInvalidEmailContactsFn = httpsCallable<
  { emails?: string[] },
  { ok: boolean; removedEmails: number; deletedContacts: number; updatedContacts: number }
>(functions, 'cleanupInvalidEmailContacts');

/** 無効アドレスを contacts から削除（複数メールなら該当のみ除去、単独なら contact 削除）。 */
export function useCleanupInvalidEmailContacts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (emails?: string[]) =>
      (await cleanupInvalidEmailContactsFn(emails ? { emails } : {})).data,
    onSuccess: () => {
      clearCollectionCache('mail_contacts');
      qc.invalidateQueries({ queryKey: ['mail_contacts'] });
    },
  });
}
